import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Gig, AuditEvent } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import { useAnalyticsClient } from '../hooks/useAnalyticsClient.js';
import { useAdminClient } from '../hooks/useAdminClient.js';

/**
 * Operations area (§27.24, §34.3): the workspace audit-event log with cursor "load more", and a
 * workspace-export button. The admin surface derives `workspaceId` from the gig — the audit and
 * export endpoints are workspace-scoped. The export document excludes secrets (§34.3) and is offered
 * as a downloadable JSON blob via an object URL.
 */

function riskClass(risk: AuditEvent['riskLevel']): string {
  return `risk-badge risk-badge--${risk}`;
}

export function OperationsPage() {
  const { gigId = '' } = useParams();
  const analytics = useAnalyticsClient();
  const admin = useAdminClient();
  const [gig, setGig] = useState<Gig | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const workspaceId = gig?.workspaceId ?? null;

  const loadPage = useCallback(
    async (wsId: string, next?: string) => {
      setLoading(true);
      setError(null);
      try {
        const page = await analytics.listAuditEvents(wsId, next);
        setEvents((prev) => (next ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load audit events.');
      } finally {
        setLoading(false);
      }
    },
    [analytics],
  );

  const init = useCallback(async () => {
    setError(null);
    try {
      const g = await admin.getGig(gigId);
      setGig(g);
      await loadPage(g.workspaceId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this workspace.');
    }
  }, [admin, gigId, loadPage]);

  useEffect(() => {
    void init();
  }, [init]);

  const onExport = useCallback(async () => {
    if (!workspaceId) return;
    setExporting(true);
    setExportNotice(null);
    setExportError(null);
    try {
      const doc = await analytics.createExport(workspaceId);
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workspace-export-${workspaceId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExportNotice('Export downloaded. Secrets are excluded (§34.3).');
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [analytics, workspaceId]);

  return (
    <main className="page page--wide">
      <header className="app-bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to={`/app/gigs/${encodeURIComponent(gigId)}`}>
              Gig editor
            </Link>{' '}
            / Operations
          </p>
          <h1>Operations</h1>
          {gig && <p className="status">{gig.name}</p>}
        </div>
        <div className="button-row">
          <Link className="button-link" to={`/app/gigs/${encodeURIComponent(gigId)}/analytics`}>
            Analytics
          </Link>
        </div>
      </header>

      <section className="card">
        <h2>Export workspace data</h2>
        <p className="help">
          Owner-requested export of configuration, repertoire, gigs, interactions, aggregate
          analytics, tips, and exportable audit history (§34.3). Secrets are excluded.
        </p>
        <div className="button-row">
          <button
            type="button"
            className="action--primary"
            disabled={!workspaceId || exporting}
            onClick={() => void onExport()}
          >
            {exporting ? 'Exporting…' : 'Export workspace data'}
          </button>
        </div>
        {exportNotice && <p className="import-result">{exportNotice}</p>}
        {exportError && <p className="error">{exportError}</p>}
      </section>

      <section className="card">
        <h2>Audit events</h2>
        <p className="help">Immutable record of security, financial, config and live-state actions (§27.24).</p>
        {error && <p className="error">{error}</p>}
        {events.length === 0 && !loading && !error ? (
          <p className="muted">No audit events.</p>
        ) : (
          <div className="audit-scroll">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Actor</th>
                  <th>Risk</th>
                  <th>Summary</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.auditEventId}>
                    <td>{e.action}</td>
                    <td>{e.targetType}</td>
                    <td>{e.actorType}</td>
                    <td>
                      <span className={riskClass(e.riskLevel)}>{e.riskLevel}</span>
                    </td>
                    <td>{e.safeSummary}</td>
                    <td className="muted">{e.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="button-row">
          {cursor && workspaceId && (
            <button
              type="button"
              disabled={loading}
              onClick={() => void loadPage(workspaceId, cursor)}
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
